"use client";

import type { FC, ReactNode } from "react";
import { useState } from "react";
import { cn } from "../../utils";
import {
  computeVariantCopy,
  computeVariantStyle,
  GameRtpListHeader,
  GameRtpListInfo,
  type GameRtpListItem,
  GameRtpListRow,
  type GameRtpListVariant,
} from "../GameRtpList";
import { Tabs, TabsContent } from "../tabs";
import GameRtpListGroupTabs from "./GameRtpListGroupTabs";

// Reserves space for the tab bar plus 5 rows, so the panel doesn't resize
// when the info panel replaces both of them.
const MOBILE_MIN_HEIGHT = "min-h-96";

export type GameRtpListGroupItemsSide = {
  title?: string;
  tabLabel?: string;
  items: GameRtpListItem[];
  infoContent?: ReactNode;
};

type Props = {
  hot: GameRtpListGroupItemsSide;
  cold: GameRtpListGroupItemsSide;
  translations?: Record<string, string>;
  onInfoClick?: () => void;
};

const GameRtpListGroupMobile: FC<Props> = ({
  hot,
  cold,
  translations,
  onInfoClick,
}) => {
  const [activeVariant, setActiveVariant] = useState<GameRtpListVariant>("hot");
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const sides = (["hot", "cold"] as const).map((variant) => {
    const side = variant === "hot" ? hot : cold;
    const copy = computeVariantCopy(variant, translations);
    return {
      ...side,
      variant,
      title: side.title ?? copy.title,
      tabLabel: side.tabLabel ?? copy.tabLabel,
    };
  });
  const activeSide =
    sides.find((side) => side.variant === activeVariant) ?? sides[0];

  const handleValueChange = (value: string) => {
    setActiveVariant(value as GameRtpListVariant);
    setIsInfoOpen(false);
  };

  const handleInfoClick = () => {
    setIsInfoOpen((open) => !open);
    onInfoClick?.();
  };

  const { bg } = computeVariantStyle(activeVariant);

  return (
    <div
      data-slot="game-rtp-list-group-mobile"
      className={cn("w-full overflow-hidden rounded-3xl p-1", bg)}
    >
      <GameRtpListHeader
        title={activeSide.title}
        variant={activeSide.variant}
        hasInfo={activeSide.infoContent != null}
        isInfoOpen={isInfoOpen}
        translations={translations}
        onInfoClick={handleInfoClick}
      />
      <Tabs
        value={activeVariant}
        onValueChange={handleValueChange}
        className={cn("px-3 pt-3 bg-background rounded-2xl", MOBILE_MIN_HEIGHT)}
      >
        {isInfoOpen ? (
          <GameRtpListInfo content={activeSide.infoContent} />
        ) : (
          <>
            <GameRtpListGroupTabs tabs={sides} />
            {sides.map((side) => (
              <TabsContent
                key={side.variant}
                value={side.variant}
                className="flex flex-col gap-2 pt-2 pb-3"
              >
                {side.items.map((item) => (
                  <GameRtpListRow
                    key={item.id}
                    item={item}
                    variant={side.variant}
                  />
                ))}
              </TabsContent>
            ))}
          </>
        )}
      </Tabs>
    </div>
  );
};

export default GameRtpListGroupMobile;
